import { useState, useEffect, memo } from "react";
import styled, { keyframes } from "styled-components";
import AdminNav from "./AdminNav";
import {
  banUser,
  getUsersList,
  unbanUser,
  updateUserBalance,
  updateUserLifeTime,
} from "../../api/user";
import { RootState } from "../../redux/type";
import { useSelector } from "react-redux";
import { motion } from "framer-motion";
import toast from "react-hot-toast";
import { demoteUser, promoteToAdmin } from "../../api/admin";
import { MarginBottom } from "../MarginBottom";
import {
  EnhancedDetailsModal,
  User,
} from "./subComponents/EnhancedDetailsModal";

// Reuse animations from AddTaskForm
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const UsersContainer = styled.div`
  width: 100%;
  background: #121212;
  backdrop-filter: blur(10px);
  border-radius: 16px;
  padding: 1rem;
  overflow-x: auto;
`;

const TableContainer = styled.div`
  overflow-x: auto;
  border-radius: 12px;
  background: rgba(23, 22, 22, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.1);
  animation: ${fadeIn} 0.4s ease-out;
  margin-bottom: 1rem;
`;

const UsersTitle = styled.h2`
  color: #ccff00;
  margin-bottom: 1rem;
  font-size: 1.6rem;
  font-weight: 700;
  text-align: center;

  @media (max-width: 768px) {
    font-size: 1.3rem;
    margin-bottom: 0.75rem;
  }
`;

const SearchBar = styled.input`
  width: 100%;
  margin: 0.5rem 0 1.25rem;
  padding: 0.8rem 1rem;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  background: rgba(255, 255, 255, 0.07);
  color: #fff;
  font-size: 1rem;

  &:focus {
    outline: none;
    border-color: #00e0ff;
    box-shadow: 0 0 0 2px rgba(0, 224, 255, 0.2);
  }

  @media (max-width: 768px) {
    font-size: 0.9rem;
    padding: 0.7rem 0.9rem;
  }
`;

const ActionButton = styled.button<{ $danger?: boolean }>`
  padding: 0.5rem 0.9rem;
  border-radius: 6px;
  border: none;
  background: ${(props) =>
    props.$danger
      ? "linear-gradient(135deg, #ff4d4d, #cc0000)"
      : "rgba(255, 255, 255, 0.15)"};
  color: #fff;
  cursor: pointer;
  font-size: 0.85rem;
  transition: all 0.25s ease-in-out;

  &:hover {
    background: ${(props) =>
      props.$danger
        ? "linear-gradient(135deg, #ff2d2d, #990000)"
        : "rgba(255, 255, 255, 0.25)"};
    transform: translateY(-1px);
    box-shadow: 0 3px 6px rgba(0, 0, 0, 0.2);
  }

  @media (max-width: 768px) {
    font-size: 0.8rem;
    padding: 0.4rem 0.7rem;
  }
`;

const Avatar = styled.div<{ name: string }>`
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: ${({ name }) => {
    const code = name.charCodeAt(0);
    return code % 2 === 0 ? "#ccff00" : "#ff4d4d";
  }};
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-weight: bold;
  font-size: 1rem;
`;

const PageButton = styled.button<{ $active?: boolean }>`
  padding: 0.45rem 0.8rem;
  border-radius: 6px;
  border: none;
  background: ${(props) =>
    props.$active ? "rgba(0, 224, 255, 0.2)" : "rgba(255, 255, 255, 0.07)"};
  color: ${(props) => (props.$active ? "#ccff00" : "#fff")};
  font-size: 0.9rem;
  cursor: pointer;

  &:hover {
    background: rgba(0, 224, 255, 0.1);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const UsersTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  color: white;
  min-width: 900px;

  @media (max-width: 768px) {
    min-width: 100%;
  }
`;

const TableRow = styled(motion.tr)`
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  transition: background-color 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
  }

  &:last-child {
    border-bottom: none;
  }
`;

const TableCell = styled.td`
  padding: 1rem;
  text-align: left;
  vertical-align: middle;
  font-size: 0.95rem;

  @media (max-width: 768px) {
    padding: 0.8rem;
    font-size: 0.85rem;
  }
`;

const TableHeader = styled.th`
  padding: 1rem;
  text-align: left;
  font-weight: 600;
  color: #ccff00;
  background: rgba(0, 224, 255, 0.1);
  position: sticky;
  top: 0;
  backdrop-filter: blur(5px);
  font-size: 0.95rem;

  @media (max-width: 768px) {
    padding: 0.8rem;
    font-size: 0.85rem;
  }
`;

const BalanceInput = styled.input`
  width: 80px;
  padding: 0.5rem;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: rgba(255, 255, 255, 0.05);
  color: white;
  font-size: 0.9rem;

  @media (max-width: 768px) {
    width: 60px;
    padding: 0.4rem;
    font-size: 0.8rem;
  }
`;

const MobileActions = styled.div`
  display: flex;
  gap: 0.3rem;
  flex-wrap: wrap;
`;

const LoadingText = styled.div`
  padding: 2rem;
  text-align: center;
  color: rgba(255, 255, 255, 0.7);
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 1.5rem;
  gap: 0.5rem;
`;

// inside EnhancedDetailsModal.tsx (or styled-components inside it)
const ModalBackdrop = styled(motion.div)`
  position: fixed; /* not absolute */
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2000; /* higher than page */
`;

const ModalContent = styled(motion.div)`
  background: #1a1a1a;
  border-radius: 16px;
  padding: 2rem;
  max-width: 800px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto; /* so content scrolls INSIDE, not outside */
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
`;

const ModalTitle = styled.h3`
  color: #ccff00;
  margin-bottom: 1.5rem;
  text-align: center;
  font-size: 1.3rem;
`;

const ModalText = styled.p`
  color: rgba(255, 255, 255, 0.8);
  text-align: center;
  margin-bottom: 1.5rem;
  line-height: 1.5;
`;

const ModalButtons = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
`;

interface ModalState {
  isOpen: boolean;
  type: "ban" | "unban" | "balance" | "lifetime" | "promote" | "demote" | null;
  uid: string | number;
  username: string;
  currentBalance?: number;
  currentLifetime?: number;
}

const AdminUsers = () => {
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingBalance, setEditingBalance] = useState<{
    [key: string]: boolean;
  }>({});
  const [users, setUsers] = useState<User[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [modal, setModal] = useState<ModalState>({
    isOpen: false,
    type: null,
    uid: null,
    username: "",
  });
  const [newBalance, setNewBalance] = useState(0);
  const [detailsModalUser, setDetailsModalUser] = useState<User | null>(null);

  const currentAdmin = useSelector((state: RootState) => state.userData);

  const [now, setNow] = useState(new Date());

  const limit = 20;
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 30000); // every 30s
    return () => clearInterval(timer);
  }, []);

  const loadAllUsers = async () => {
    try {
      setLoading(true);
      const result = await getUsersList(currentAdmin?.uid || "", 1, 99999); // large limit
      if (!result.error) {
        setAllUsers(result.users);
        setTotalPages(Math.ceil(result.total / limit));
      }
    } catch (error) {
      toast.error("Error loading all users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAllUsers();
  }, [page]);

  const filteredUsers = allUsers.filter(
    (user) =>
      user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.uid.toString().includes(searchTerm)
  );

  const paginatedUsers = filteredUsers.slice((page - 1) * limit, page * limit);

  const closeModal = () => {
    loadAllUsers();
    setModal({
      isOpen: false,
      type: null,
      uid: null,
      username: "",
    });
  };

  const handleBanUser = async () => {
    if (!modal.uid) return;

    try {
      await banUser(modal.uid, "Admin action", currentAdmin?.uid);
      setUsers(
        users.map((user) =>
          user.uid === modal.uid ? { ...user, banned: true } : user
        )
      );
      toast.success("User banned successfully");
    } catch (error) {
      toast.error(error.message || "Error banning user");
    } finally {
      closeModal();
    }
  };

  const handleUnbanUser = async () => {
    if (!modal.uid) return;

    try {
      await unbanUser(modal.uid, currentAdmin?.uid);
      setUsers(
        users.map((user) =>
          user.uid === modal.uid ? { ...user, banned: false } : user
        )
      );
      toast.success("User unbanned successfully");
    } catch (error) {
      toast.error(error.message || "Error unbanning user");
    } finally {
      closeModal();
    }
  };

  const handleUpdateBalance = async () => {
    if (!modal.uid || !modal.currentBalance) return;

    try {
      await updateUserBalance(modal.uid, newBalance, currentAdmin?.uid);
      setUsers(
        users.map((user) =>
          user.uid === modal.uid ? { ...user, points: newBalance } : user
        )
      );
      setEditingBalance({ ...editingBalance, [modal.uid]: false });
      toast.success("Balance updated successfully");
    } catch (error) {
      toast.error(error.message || "Error updating balance");
    } finally {
      closeModal();
    }
  };

  const handleUpdateLifelineTime = async () => {
    if (!modal.uid || modal.currentLifetime === undefined) return;

    try {
      await updateUserLifeTime(modal.uid, newBalance, currentAdmin?.uid);
      setUsers(
        users.map((user) =>
          user.uid === modal.uid ? { ...user, lifelineTime: newBalance } : user
        )
      );
      setEditingBalance({ ...editingBalance, [`life-${modal.uid}`]: false });
      toast.success("Lifeline time updated successfully");
    } catch (error) {
      toast.error(error.message || "Error updating lifeline time");
    } finally {
      closeModal();
    }
  };

  const handlePromoteToAdmin = async () => {
    if (!modal.uid) return;

    try {
      await promoteToAdmin(modal.uid, currentAdmin?.uid);
      setUsers(
        users.map((user) =>
          user.uid === modal.uid ? { ...user, role: "admin" } : user
        )
      );
      toast.success("User promoted to admin successfully");
    } catch (error) {
      toast.error(error.message || "Error promoting user");
    } finally {
      closeModal();
    }
  };

  const handleDemoteUser = async () => {
    if (!modal.uid) return;

    try {
      await demoteUser(modal.uid, currentAdmin?.uid);
      setUsers(
        users.map((user) =>
          user.uid === modal.uid ? { ...user, role: "user" } : user
        )
      );
      toast.success("User demoted successfully");
    } catch (error) {
      toast.error(error.message || "Error demoting user");
    } finally {
      closeModal();
    }
  };

  const renderModalContent = () => {
    switch (modal.type) {
      case "ban":
        return (
          <>
            <ModalTitle>Confirm Ban</ModalTitle>
            <ModalText>
              Are you sure you want to ban {modal.username}? They will no longer
              be able to access the app.
            </ModalText>
            <ModalButtons>
              <ActionButton $danger onClick={handleBanUser}>
                Confirm Ban
              </ActionButton>
              <ActionButton onClick={closeModal}>Cancel</ActionButton>
            </ModalButtons>
          </>
        );
      case "unban":
        return (
          <>
            <ModalTitle>Confirm Unban</ModalTitle>
            <ModalText>
              Are you sure you want to unban {modal.username}? They will regain
              access to the app.
            </ModalText>
            <ModalButtons>
              <ActionButton onClick={handleUnbanUser}>
                Confirm Unban
              </ActionButton>
              <ActionButton onClick={closeModal}>Cancel</ActionButton>
            </ModalButtons>
          </>
        );
      case "balance":
        return (
          <>
            <ModalTitle>Update Balance</ModalTitle>
            <ModalText>
              Update balance for {modal.username}. Current balance:{" "}
              {modal.currentBalance}
            </ModalText>
            <BalanceInput
              type="number"
              value={newBalance}
              onChange={(e) => setNewBalance(Number(e.target.value))}
              autoFocus
            />
            <ModalButtons style={{ marginTop: "1rem" }}>
              <ActionButton onClick={handleUpdateBalance}>Update</ActionButton>
              <ActionButton onClick={closeModal}>Cancel</ActionButton>
            </ModalButtons>
          </>
        );
      case "promote":
        return (
          <>
            <ModalTitle>Confirm Promotion</ModalTitle>
            <ModalText>
              Are you sure you want to promote {modal.username} to admin? They
              will gain full admin privileges.
            </ModalText>
            <ModalButtons>
              <ActionButton onClick={handlePromoteToAdmin}>
                Confirm Promotion
              </ActionButton>
              <ActionButton onClick={closeModal}>Cancel</ActionButton>
            </ModalButtons>
          </>
        );
      case "demote":
        return (
          <>
            <ModalTitle>Confirm Demotion</ModalTitle>
            <ModalText>
              Are you sure you want to demote {modal.username} from admin? They
              will lose admin privileges.
            </ModalText>
            <ModalButtons>
              <ActionButton $danger onClick={handleDemoteUser}>
                Confirm Demote
              </ActionButton>
              <ActionButton onClick={closeModal}>Cancel</ActionButton>
            </ModalButtons>
          </>
        );
      case "lifetime":
        return (
          <>
            <ModalTitle>Update Lifetime</ModalTitle>
            <ModalText>
              Update lifetime (in hours) for {modal.username}. Current:{" "}
              {modal.currentLifetime} hrs
            </ModalText>
            <BalanceInput
              type="number"
              value={newBalance}
              onChange={(e) => setNewBalance(Number(e.target.value))}
              autoFocus
            />
            <ModalButtons style={{ marginTop: "1rem" }}>
              <ActionButton onClick={handleUpdateLifelineTime}>
                Update
              </ActionButton>
              <ActionButton onClick={closeModal}>Cancel</ActionButton>
            </ModalButtons>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <AdminNav />
      <UsersContainer>
        <UsersTitle>User Management</UsersTitle>

        <SearchBar
          type="text"
          placeholder="Search users..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />

        {loading ? (
          <LoadingText>Loading users...</LoadingText>
        ) : (
          <>
            <TableContainer>
              <UsersTable>
                <thead>
                  <tr>
                    <TableHeader>#</TableHeader>
                    <TableHeader>Username</TableHeader>
                    <TableHeader>Balance</TableHeader>
                    <TableHeader>Actions</TableHeader>
                  </tr>
                </thead>

                <tbody>
                  {paginatedUsers.map((user, index) => (
                    <TableRow key={user.telegramId}>
                      <TableCell>{(page - 1) * limit + index + 1}</TableCell>
                      <TableCell>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "0.5rem",
                          }}
                        >
                          {user.profilePicture ? (
                            <img
                              src={user.profilePicture}
                              alt={user.username}
                              style={{
                                width: "30px",
                                height: "30px",
                                borderRadius: "50%",
                                objectFit: "cover",
                              }}
                            />
                          ) : (
                            <Avatar name={user.username}>
                              {user.username.charAt(0).toUpperCase()}
                            </Avatar>
                          )}
                          {user.username}
                        </div>
                      </TableCell>

                      <TableCell>{user.points}</TableCell>

                      <TableCell>
                        <MobileActions>
                          <ActionButton
                            onClick={() => setDetailsModalUser(user)}
                            style={{
                              background: "rgba(0, 224, 255, 0.15)",
                            }}
                          >
                            Details
                          </ActionButton>
                        </MobileActions>
                      </TableCell>
                    </TableRow>
                  ))}
                </tbody>
              </UsersTable>
            </TableContainer>
            {totalPages > 1 && (
              <Pagination>
                <PageButton
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  Previous
                </PageButton>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const pageNum =
                    page <= 3
                      ? i + 1
                      : page >= totalPages - 2
                      ? totalPages - 4 + i
                      : page - 2 + i;
                  return pageNum > 0 && pageNum <= totalPages ? (
                    <PageButton
                      key={pageNum}
                      $active={page === pageNum}
                      onClick={() => setPage(pageNum)}
                    >
                      {pageNum}
                    </PageButton>
                  ) : null;
                })}
                <PageButton
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                >
                  Next
                </PageButton>
              </Pagination>
            )}
          </>
        )}
      </UsersContainer>
      <MarginBottom />

      {modal.isOpen && (
        <ModalBackdrop
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={closeModal}
        >
          <ModalContent
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            {renderModalContent()}
          </ModalContent>
        </ModalBackdrop>
      )}

      {detailsModalUser && (
        <EnhancedDetailsModal
          user={detailsModalUser}
          onClose={() => setDetailsModalUser(null)}
          onUserUpdate={(updatedUser) => {
            setUsers((prev) =>
              prev.map((u) => (u.uid === updatedUser.uid ? updatedUser : u))
            );
            setDetailsModalUser(updatedUser);
          }}
        />
      )}
    </>
  );
};

export default AdminUsers;
